'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { calcularPontoEquilibrio } from '@/lib/calculos/ponto-equilibrio'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  SalvarCalculoCta,
  Eyebrow,
  type ItemLista,
  ListaItens,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  SoftExportBar,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoPayload } from '@/lib/export/types'

export function PontoClient() {
  const [custosFixos, setCustosFixos] = useState<ItemLista[]>([{ id: 1, desc: '', valor: '' }])
  const [precoVenda, setPrecoVenda] = useState('')
  const [custoVariavel, setCustoVariavel] = useState('')

  // Pré-preenche com o que veio do console da home (?preco=&custovar=).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const pv = p.get('preco')
    const cv = p.get('custovar')
    if (pv) setPrecoVenda(pv)
    if (cv) setCustoVariavel(cv)
  }, [])
  const nextId = useRef(2)

  const addCusto = (desc = '') =>
    setCustosFixos((l) => [...l, { id: nextId.current++, desc, valor: '' }])
  const removeCusto = (id: number) => setCustosFixos((l) => l.filter((c) => c.id !== id))
  const updateCusto = (id: number, field: 'desc' | 'valor', val: string) =>
    setCustosFixos((l) => l.map((c) => (c.id === id ? { ...c, [field]: val } : c)))

  const r = useMemo(
    () =>
      calcularPontoEquilibrio({
        custosFixos: custosFixos.map((c) => ({ descricao: c.desc, valor: parseValor(c.valor) })),
        precoVenda: parseValor(precoVenda),
        custoVariavel: parseValor(custoVariavel),
      }),
    [custosFixos, precoVenda, custoVariavel]
  )

  const unidadesAnim = useAnimatedNumber(r.peUnidades ?? 0)

  /** Guarda da barra mobile: sem isto ela nascia fixa no rodapé mostrando 0. */
  const temResultado = parseValor(precoVenda) > 0 && parseValor(custoVariavel) >= 0

  useCalcTelemetry(
    'ponto_equilibrio',
    parseValor(precoVenda) > 0 && parseValor(custoVariavel) >= 0,
    r.prejuizoPorUnidade
      ? 'Preço não cobre o custo por unidade'
      : `Venda ${(r.peUnidades ?? 0).toLocaleString('pt-BR')} un. pra empatar`,
  )

  const buildCartao = (): CartaoPayload =>
    r.prejuizoPorUnidade
      ? {
          nome: 'ponto-de-equilibrio',
          heroLabel: 'Sem ponto de equilíbrio',
          heroValue: '—',
          tone: 'neg',
          linhas: [
            { label: 'Margem de contribuição', value: brl(r.margemContribuicao), strong: true, tone: 'neg' },
          ],
        }
      : {
          nome: 'ponto-de-equilibrio',
          heroLabel: 'Venda no mínimo',
          heroValue: (r.peUnidades ?? 0).toLocaleString('pt-BR'),
          heroSuffix: '/mês',
          tone: 'brand',
          linhas: [
            { label: 'ou em faturamento', value: brl(r.peReais ?? 0) },
            { label: 'Custos fixos', value: brl(r.custosFixosTotal) },
            { label: 'Cada venda deixa', value: brl(r.margemContribuicao) },
            {
              label: 'Vender por mês',
              value: `${(r.peUnidades ?? 0).toLocaleString('pt-BR')} un.`,
              strong: true,
              tone: 'brand',
            },
          ],
        }

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <ListaItens
          label="Seus custos fixos do mês"
          items={custosFixos}
          onAdd={addCusto}
          onRemove={removeCusto}
          onUpdate={updateCusto}
          sugestoes={['Aluguel', 'Internet', 'DAS (imposto)', 'Energia', 'Salários', 'Sistema']}
          descPlaceholder="Ex.: Aluguel"
          addLabel="Adicionar custo fixo"
          hint="O que você paga todo mês independente de vender — aluguel, internet, DAS…"
        />
        <MoneyField
          label="Preço médio de venda"
          value={precoVenda}
          onChange={setPrecoVenda}
          hint="Quanto custa, em média, cada produto/serviço que você vende."
        />
        <MoneyField
          label="Custo variável por venda"
          value={custoVariavel}
          onChange={setCustoVariavel}
          hint="O que sai do bolso a cada venda: material, comissão, imposto por unidade."
        />
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="scale" tone="muted" className="mb-3">
          Ponto de equilíbrio
        </Eyebrow>

        <ResultCard
          tone={r.prejuizoPorUnidade ? 'neg' : 'brand'}
          heroLabel={r.prejuizoPorUnidade ? 'Preço abaixo do custo' : 'Venda no mínimo'}
          heroValue={
            r.prejuizoPorUnidade
              ? '—'
              : `${Math.round(unidadesAnim).toLocaleString('pt-BR')}`
          }
          heroSuffix={r.prejuizoPorUnidade ? undefined : '/mês'}
        >
          {r.prejuizoPorUnidade ? (
            <p className="py-2 text-[13px] text-ink-mute">
              Seu preço está igual ou abaixo do custo variável — cada venda perde dinheiro, então
              não existe ponto de equilíbrio. Aumente o preço ou reduza o custo por venda.
            </p>
          ) : (
            <>
              <DetailRow label="ou em faturamento" value={brl(r.peReais ?? 0)} />
              <DetailRow label="Custos fixos" value={brl(r.custosFixosTotal)} />
              <DetailRow
                label="Cada venda deixa"
                value={brl(r.margemContribuicao)}
              />
              <RowStrong
                label={`Vender por mês`}
                value={`${(r.peUnidades ?? 0).toLocaleString('pt-BR')} un.`}
                tone="brand"
              />
            </>
          )}
        </ResultCard>

        {!r.prejuizoPorUnidade && (
          <AvisoBox tone="brand" icon="bulb" className="mt-4">
            Cada venda deixa {brl(r.margemContribuicao)} pra pagar suas contas fixas. Você precisa de{' '}
            {(r.peUnidades ?? 0).toLocaleString('pt-BR')} vendas pra cobrir tudo — a partir daí, é lucro.
          </AvisoBox>
        )}
        {r.prejuizoPorUnidade && (
          <AvisoBox tone="neg" icon="alert" className="mt-4">
            Com preço abaixo do custo variável, vender mais só aumenta o prejuízo. Reveja o preço
            antes de qualquer coisa.
          </AvisoBox>
        )}

        <SoftExportBar buildCartao={buildCartao} />
        <LeadCapture show={parseValor(precoVenda) > 0} source="ponto-de-equilibrio" alertType="das" />
        <SalvarCalculoCta
          show={parseValor(precoVenda) > 0}
          source="ponto-de-equilibrio"
          hook="Ponto de equilíbrio na mão. No Premium você acompanha o mês de verdade."
        />
      </section>

      {temResultado && <MobileResultBar payload={buildCartao()} cardRef={cardRef} />}
    </div>
  )
}
